// askSmart-ui/src/pages/TOSP/revenueBudget.ts
//
// The Revenue Budget: twelve monthly figures per income account, projected
// from the leases already in TOSP. This is the source of truth for revenue —
// the admin reviews it, keys it into QuickBooks, then exports the completed
// budget and imports the EXPENSE side back. Nothing here reads the QBO file.
//
// Every line names the QuickBooks account it feeds, so the screen can be read
// straight down the QBO chart of accounts while typing.
//
// Lines that cannot yet be projected are returned with zeroed months and a
// `gap` explaining what's missing, rather than being hidden or guessed at. A
// budget that quietly omits a revenue stream is worse than one that says the
// stream is missing.

import {
  BASE_RENT_SPACE_TYPES,
  bmvaAnnualFee,
  bmvaRoyalty,
  chargesPercentageRent,
  LeasedSpace,
  occupancyDayShare,
  rollPartsForUnit,
  spaceEscalation,
  Tenant,
  tenantLabel,
  tenantReportsSales,
  TaxRollRecord,
  TospConfiguration,
  unitBudgetTotal,
  Unit,
  rentPsfOn,
} from "./types";
import { possessionOf } from "./budgetAllocation";
import { sameText } from "./validation";
import { tenantBreakpointDetail } from "./portal/portalSummary";
import { TenantSalesMonth } from "./portal/tenantSales";

export const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export type RevenueLineKey =
  | "baseRent"
  | "storageRent"
  | "percentageRent"
  | "realEstateTaxes"
  | "bmvaRoyalty"
  | "bmvaBasicFees";

/** Where a line's figures come from, which decides how much to trust them. */
export type RevenueSource = "lease" | "taxRolls" | "sales";

/**
 * One TENANT's share of a line. Deliberately per tenant and not per lease: a
 * tenant may hold four occupancies — commercial, patio, storage, mezzanine —
 * and what's wanted is what they owe altogether, not a row per unit.
 */
export interface RevenueContribution {
  id: string;
  label: string;
  months: number[];
  total: number;
  /**
   * Months that could NOT be worked out, as distinct from months worked out to
   * be nothing.
   *
   * A tenant with no sales history has an unknown percentage rent; a tenant
   * projected to stay under their breakpoint has one of zero. Both read as 0
   * in `months`, and they are not the same statement — one is a gap for the
   * admin to fill, the other is an answer.
   */
  unknown: boolean[];
}

export interface RevenueLine {
  key: RevenueLineKey;
  label: string;
  /** The QuickBooks account this line is keyed into. */
  qboAccount: string;
  /** The parent account it sits under, as QuickBooks groups them. */
  group: string;
  source: RevenueSource;
  months: number[];
  total: number;
  /** Set when the line can't be projected yet — the months will all be zero. */
  gap?: string;
  contributions: RevenueContribution[];
}

export interface RevenueProjection {
  year: number;
  lines: RevenueLine[];
  /** Monthly totals across every line, and the year. */
  months: number[];
  total: number;
  /**
   * Units with no lease running in the budget year. There are no vacancies in
   * this portfolio, so these are missing lease data rather than empty space —
   * every one of them is revenue the budget is silently short.
   */
  unleasedUnits: string[];
  /**
   * The two unit-borne recoveries, by UNIT rather than by who pays — unitId →
   * twelve monthly figures, each unit's own charge plus any share of an unlet
   * unit it absorbed.
   *
   * What a handover divides. The budget charges one tenancy for the year, and
   * when the space changes hands the two tenancies' statements split these
   * figures by the months each held it: the same money, read by space instead
   * of by payer. Not stored — derived with the projection, every time.
   */
  perUnit: {
    realEstateTaxes: Map<string, number[]>;
    bmvaBasicFees: Map<string, number[]>;
  };
  /** unitId → the tenancy the budget charged for it: the holder at 1 January. */
  incumbents: Map<string, string>;
}

const pad = (n: number) => String(n).padStart(2, "0");
const zeroes = (): number[] => new Array(12).fill(0);
const falses = (): boolean[] => new Array(12).fill(false);
const trues = (): boolean[] => new Array(12).fill(true);
const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);
/** For the notes on a line, where a figure reads better than a unit count. */
const money = (value: number): string =>
  value.toLocaleString(undefined, { style: "currency", currency: "USD" });

const monthStart = (year: number, month: number) => `${year}-${pad(month + 1)}-01`;
const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const monthEnd = (year: number, month: number) =>
  `${year}-${pad(month + 1)}-${pad(daysInMonth(year, month))}`;

// How much of a month a lease is actually on foot for. A lease that commences
// or ends mid-month is billed for its share of that month, so the commencement
// month doesn't overstate the year by most of a month's rent.
function activeFraction(
  year: number,
  month: number,
  startISO: string,
  endISO: string | null,
): number {
  const first = monthStart(year, month);
  const last = monthEnd(year, month);
  if (!startISO || startISO > last) return 0;
  if (endISO && endISO < first) return 0;

  const days = daysInMonth(year, month);
  const from = startISO > first ? Number(startISO.slice(8, 10)) : 1;
  const to = endISO && endISO < last ? Number(endISO.slice(8, 10)) : days;
  if (!from || !to || to < from) return 0;
  return (to - from + 1) / days;
}

// Every leased space in the portfolio, with the lease that governs it and the
// unit it's billed through.
//
// One row per space, not per lease: rent is charged on a space's own rate and
// its unit's area, and a lease covering a ground floor, a basement and a
// storage locker earns three different amounts. The rows are collapsed back to
// one line per tenant further down, which is where the lease's spaces add up
// again.
//
// A space with no unit assigned is skipped outright. That's the terrace that
// generates neither rent nor taxes — there is no area to bill and no roll to
// recover against, so it contributes nothing rather than zero.
interface Lease {
  tenant: Tenant;
  unit: Unit;
  occupancy: Tenant["occupancies"][number];
  space: LeasedSpace;
}

function leasesFor(units: Unit[], tenants: Tenant[]): Lease[] {
  const unitById = new Map(units.map((u) => [u.id, u]));
  return tenants.flatMap((tenant) =>
    (tenant.occupancies || []).flatMap((occupancy) =>
      (occupancy.leasedSpaces || []).flatMap((space) => {
        const unit = space.unitId ? unitById.get(space.unitId) : undefined;
        return unit ? [{ tenant, unit, occupancy, space }] : [];
      }),
    ),
  );
}

/** A row on its way to becoming a contribution, before rows are merged. */
type ContributionDraft = {
  id: string;
  label: string;
  months: number[];
  /** Omitted where every month is known — most lines. */
  unknown?: boolean[];
};

// ─── Shared helpers ──────────────────────────────────────────────────────────

function totalOf(contributions: RevenueContribution[]): number[] {
  return zeroes().map((_, m) => sum(contributions.map((c) => c.months[m])));
}

/**
 * Alphabetical, like every other list in the module.
 *
 * Was by size, which reads well as a chart and badly as a worksheet: an admin
 * looking for one tenant had to scan the whole column, and the order moved
 * whenever a figure changed.
 */
const sortByName = (rows: RevenueContribution[]) =>
  [...rows].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Collapses rows to one per tenant, summing month by month. A tenant holding a
 * shop, its patio and a storage locker appears once, for the whole of what they
 * owe on that line — which unit each part came from is clutter here.
 */
function byTenant(rows: ContributionDraft[]): RevenueContribution[] {
  const merged = new Map<string, RevenueContribution>();
  for (const row of rows) {
    const rowUnknown = row.unknown || falses();
    const existing = merged.get(row.id);
    if (existing) {
      row.months.forEach((amount, m) => {
        existing.months[m] += amount;
        // One known figure makes the month known: a tenant whose shop can be
        // projected and whose locker cannot is not an unknown, it is a partial.
        existing.unknown[m] = existing.unknown[m] && rowUnknown[m];
      });
      existing.total = sum(existing.months);
    } else {
      const months = [...row.months];
      merged.set(row.id, {
        id: row.id,
        label: row.label,
        months,
        total: sum(months),
        unknown: [...rowUnknown],
      });
    }
  }
  return sortByName([...merged.values()]);
}

/** A unit and what it carries on one recovery line for the year. */
interface Assessed {
  unit: Unit;
  annual: number;
}

/** A recovery line divided two ways: by who pays, and by which space. */
interface RecoveryAllocation {
  /** One row per tenancy, which is what the budget line shows. */
  rows: ContributionDraft[];
  /**
   * unitId → twelve monthly figures, the unit's own charge plus any share of
   * an unlet unit it absorbed. The basis a handover is divided on, and the
   * reason this is kept: a unit that changed hands is one set of figures split
   * between two tenancies, not two sets.
   */
  perUnit: Map<string, number[]>;
  /** Assessed on units nobody held, in buildings with nobody to charge. */
  unrecovered: number;
}

/**
 * A recovery line's rows: every unit's annual charge handed to the tenancy that
 * held it when the year opened, in twelve equal instalments.
 *
 * THE SAME RULE AS CAM, FOR THE SAME REASON. A budget is drawn up before the
 * year starts and assumes whoever holds a unit pays for it all year, so a
 * handover in July moves nothing and the incoming tenant is not on the budget
 * at all. What actually happened is settled at the year-end reconciliation.
 * This replaces splitting the charge day by day between the tenancies that
 * turned out to run in the year — right for a statement of fact, wrong for a
 * forecast, and it had every budget quietly restating itself as leases changed.
 *
 * A RECOVERY IS NEVER THE LANDLORD'S COST. A unit nobody held when the year
 * opened has no tenancy to charge, so its assessment is carried by the LET
 * units of its own building, in proportion to what they themselves carry on
 * this line. BH4 is the case: a new building, LCBO in #1 and #2 standing empty
 * until August, so the building's taxes and dues are recovered from the one
 * tenant there was to charge. The building is the limit of it — these charges
 * are assessed building by building, and a tenant in another building has
 * nothing to do with them.
 *
 * Only where a building has no let unit at all does the charge stay
 * unrecovered, as a named "Unoccupied" row. There is nobody to bill, and the
 * line must not pretend otherwise.
 */
function recoveryRows(
  assessed: Assessed[],
  incumbents: Map<string, Lease>,
): RecoveryAllocation {
  // WHAT EACH UNIT CARRIES, which is the whole of the arithmetic. The tenancy
  // rows are then simply this read through who held each unit — and the same
  // figures read through who held it in a GIVEN MONTH are what a handover
  // divides, which is why this is returned as well as the rows.
  const carried = new Map<string, number>();
  const add = (unitId: string, annual: number): void =>
    void carried.set(unitId, (carried.get(unitId) || 0) + annual);

  // What a building has to recover from somebody other than the unit it was
  // assessed on, because that unit was not let when the year opened.
  const orphaned = new Map<string, number>();
  for (const { unit, annual } of assessed) {
    if (incumbents.has(unit.id)) add(unit.id, annual);
    else orphaned.set(unit.buildingId, (orphaned.get(unit.buildingId) || 0) + annual);
  }

  let unrecovered = 0;
  const stranded = new Map<string, number>();
  for (const [buildingId, amount] of orphaned) {
    const carrying = assessed.filter(
      (row) => row.unit.buildingId === buildingId && incumbents.has(row.unit.id),
    );
    const total = carrying.reduce((running, row) => running + row.annual, 0);
    if (carrying.length === 0 || total <= 0) {
      // Nobody in the building to carry it. Shown rather than spread onto
      // another building's tenants, who have no part in this assessment.
      stranded.set(buildingId, (stranded.get(buildingId) || 0) + amount);
      unrecovered += amount;
      continue;
    }
    carrying.forEach(({ unit, annual }) => add(unit.id, (amount * annual) / total));
  }

  const perUnit = new Map<string, number[]>();
  carried.forEach((annual, unitId) =>
    perUnit.set(unitId, zeroes().map(() => annual / 12)),
  );

  const byRow = new Map<string, ContributionDraft>();
  const charge = (id: string, label: string, months: number[]): void => {
    let row = byRow.get(id);
    if (!row) {
      row = { id, label, months: zeroes() };
      byRow.set(id, row);
    }
    months.forEach((amount, m) => {
      row.months[m] += amount;
    });
  };

  perUnit.forEach((months, unitId) => {
    const lease = incumbents.get(unitId) as Lease;
    charge(lease.tenant.id, tenantLabel(lease.tenant), months);
  });
  stranded.forEach((annual, buildingId) =>
    charge(`building-${buildingId}`, "Unoccupied", zeroes().map(() => annual / 12)),
  );

  return { rows: [...byRow.values()], perUnit, unrecovered };
}

/** The QuickBooks parent accounts the income lines sit under. */
const RENTAL_INCOME = "4000 Rental Income";
export const BILLABLE_INCOME = "4100 Billable Expense Income";

/** The CAM recovery line, which the budget's own expenses produce. */
export const CAM_RECOVERY_KEY = "camRecovery";
export const CAM_RECOVERY_ACCOUNT = "41001 CAMs";
export const CAM_RECOVERY_LABEL = "CAMs Recovered";

/**
 * Slots the stored CAM recovery into a set of revenue lines.
 *
 * It belongs with the other recoveries under 4100 and sorts first by account
 * number, so it goes immediately before Real Estate Taxes (41002). The panel
 * draws its group headers by ADJACENCY, so a 4100 line appended after the
 * 4000s would print a second "Billable Expense Income" header — hence placing
 * it rather than pushing it.
 *
 * Applied to a SNAPSHOT as well as to the live projection, and idempotent: a
 * snapshot taken after the CAM step already holds its own frozen 41001, and
 * that one wins. A snapshot taken BEFORE the step existed does not, and
 * without this the recovery would be invisible on any budget that had once
 * been reviewed — the figures would be stored, the step ticked, and nothing
 * would appear.
 */
export function withCamRecovery<
  T extends { group: string; key?: string; qboAccount?: string },
>(lines: T[], camRecovery: T | undefined): T[] {
  if (!camRecovery) return lines;
  const already = lines.some(
    (line) => line.key === CAM_RECOVERY_KEY || line.qboAccount === CAM_RECOVERY_ACCOUNT,
  );
  if (already) return lines;
  const at = lines.findIndex((line) => line.group === BILLABLE_INCOME);
  if (at < 0) return [...lines, camRecovery];
  return [...lines.slice(0, at), camRecovery, ...lines.slice(at)];
}

// ─── Rent ────────────────────────────────────────────────────────────────────

/**
 * The lease holding each unit when the budget year opened — unitId → lease.
 *
 * A BUDGET IS DRAWN UP BEFORE THE YEAR STARTS, AND IT DOES NOT KNOW. Whoever
 * holds a unit when the budget is prepared is assumed to hold it all year, so
 * the rent is theirs for twelve months and a later handover moves nothing. A
 * tenant arriving part-way through the year was not in the budget at all and
 * does not appear on it; what actually happened is settled at the year-end
 * reconciliation.
 *
 * Decided on POSSESSION, not on rent: the same rule the CAM line uses, and the
 * same helper, so the two can never name different incumbents. LCBO is why —
 * their lease commenced in August 2024 and their rent in February 2025, and
 * reading incumbency from rent would have left their unit with nobody holding
 * it in January and dropped the whole year's rent off the budget.
 *
 * The largest January share wins where a handover fell inside that month, and
 * the key is the UNIT rather than the tenancy: two leases on one unit — a term
 * and the agreement that replaced it — would otherwise each bill a full year
 * for the same space.
 */
function incumbentLeases(
  year: number,
  leases: Lease[],
  /** unitId → tenantId: units whose occupancy the budget STATES. */
  assume: Record<string, string> = {},
): Map<string, Lease> {
  const best = new Map<string, { lease: Lease; share: number }>();
  for (const lease of leases) {
    const share = occupancyDayShare(
      lease.occupancy,
      possessionOf(lease.occupancy, lease.space),
      year,
      1,
    );
    if (share <= 0.005) continue;
    const held = best.get(lease.unit.id);
    if (!held || share > held.share) best.set(lease.unit.id, { lease, share });
  }
  const incumbents = new Map(
    [...best.entries()].map(([unitId, held]) => [unitId, held.lease]),
  );

  // WHAT THE BUDGET SAYS BEATS WHAT THE DATES SAY. An admin stating who was
  // assumed to be paying for a unit is the budget's own knowledge — the lease
  // may have been signed the previous autumn for a space handed over in
  // August, which is exactly how the old budget had Greekery in BH4 182 #2
  // from January. Stated for a unit nobody held at the turn of the year, it
  // puts that unit back on the budget; stated against an incumbent, it
  // replaces them.
  for (const [unitId, tenantId] of Object.entries(assume)) {
    const stated = leases.find(
      (lease) => lease.unit.id === unitId && lease.tenant.id === tenantId,
    );
    if (stated) incumbents.set(unitId, stated);
  }

  return incumbents;
}

// Area × the rate in force on the 1st of each month, over twelve. The rate
// already accounts for step-ups, renewals and rent resets, since it comes from
// the same rentPsfOn the Occupancy card displays — so a lease that renews or
// steps up mid-year shows the change in the month it happens.
function rentContribution(
  year: number,
  { tenant, unit, occupancy, space }: Lease,
): ContributionDraft | null {
  const escalation = spaceEscalation(occupancy, space);
  const months = zeroes();
  // Every month is a statement the budget is making: either rent at the rate
  // the lease sets, or a known nothing before the rent commences. The one
  // genuine unknown left is a space with no commencement rate recorded, which
  // nobody can price.
  const unknown = falses();

  for (let m = 0; m < 12; m += 1) {
    // NO END DATE. The term's expiry is not a handover the budget can foresee:
    // a lease running out in June is assumed to carry on, because that is what
    // was assumed when the budget was prepared. The space's own commencement
    // still applies — a rent-free fit-out period is a known nil, not a guess.
    const fraction = activeFraction(year, m, space.rentCommenceDate, null);
    if (fraction === 0) continue;
    const psf = rentPsfOn(escalation, monthStart(year, m));
    if (psf === null) {
      unknown[m] = true;
      continue;
    }
    months[m] = (unit.occupancySqFt * psf * fraction) / 12;
  }

  if (sum(months) === 0) return null;
  return { id: tenant.id, label: tenantLabel(tenant), months, unknown };
}

// Rent splits by the unit's Space Type, not by any field on the lease:
// trading space (Commercial and Enclosed Patio) is Base Rent, and every other
// rent-generating space — Mezzanine, Basement, Storage — is Storage Rent. Both
// are worked out identically; only the QuickBooks account they land in differs.
function rentLines(
  year: number,
  leases: Lease[],
  incumbents: Map<string, Lease>,
): [RevenueLine, RevenueLine] {
  const base: ContributionDraft[] = [];
  const storage: ContributionDraft[] = [];

  for (const lease of leases) {
    // Only the incumbent's lease earns on the budget. A lease that commenced
    // after the year opened belongs to a handover the budget could not have
    // known about, and one that never held the unit in January earns nothing.
    if (incumbents.get(lease.unit.id) !== lease) continue;
    const contribution = rentContribution(year, lease);
    if (!contribution) continue;
    const isBaseRent = BASE_RENT_SPACE_TYPES.some((type) =>
      sameText(lease.unit.spaceType, type),
    );
    (isBaseRent ? base : storage).push(contribution);
  }

  const line = (
    key: RevenueLineKey,
    label: string,
    qboAccount: string,
    rows: ContributionDraft[],
  ): RevenueLine => {
    const contributions = byTenant(rows);
    const months = totalOf(contributions);
    return {
      key,
      label,
      qboAccount,
      group: RENTAL_INCOME,
      source: "lease",
      months,
      total: sum(months),
      contributions,
    };
  };

  return [
    line("baseRent", "Base Rent", "40001 Base Rent", base),
    line("storageRent", "Storage Rent", "40002 Storage Rent", storage),
  ];
}

// ─── Real Estate Taxes ───────────────────────────────────────────────────────

// The recovery billed on to tenants. The TAX ROLLS TAB IS THE SOURCE OF TRUTH:
// the budget figure is last year's final bill plus the agreed uplift, and that
// is exactly what Budget PA on the relevant year's roll already holds. So this
// reads Budget PA and nothing else — deliberately NOT Interim + Final, which is
// what was actually billed rather than what is being budgeted.
function realEstateTaxLine(
  year: number,
  units: Unit[],
  incumbents: Map<string, Lease>,
  taxRolls: TaxRollRecord[],
): { line: RevenueLine; perUnit: Map<string, number[]> } {
  const assessed: Assessed[] = [];
  let missing = 0;

  for (const unit of units) {
    // Budget PA is struck per roll share, so the unit's figure is the sum of
    // the shares it holds. Its own taxYears record only carries what was typed
    // by hand before budgets moved onto the rolls — reading that alone left
    // every unit looking unbudgeted the moment a year was opened properly.
    const annual =
      unitBudgetTotal(taxRolls, unit.id, year) ??
      (unit.taxYears || []).find((y) => y.year === year)?.budgetPa ??
      0;
    if (annual === 0) {
      // A unit holding no roll share at all is not missing a figure: it is not
      // separately assessed, which is ordinary for a locker or a patio. Only a
      // unit that IS assessed and has no Budget PA for the year is a gap, and
      // counting the rest turned a complete register into a warning about
      // twenty-one units nobody needs to do anything about.
      //
      // The converse case — a roll assigned to NO unit — belongs on the Tax
      // Rolls tab, which marks it when the register is loaded.
      if (rollPartsForUnit(taxRolls, unit.id).length > 0) missing += 1;
      continue;
    }
    assessed.push({ unit, annual });
  }

  // A twelfth a month, charged to whoever held the unit when the year opened —
  // and where nobody did, to the let units of that building.
  const { rows, perUnit, unrecovered } = recoveryRows(assessed, incumbents);
  const contributions = byTenant(rows);
  const months = totalOf(contributions);
  const stranded =
    unrecovered > 0.005
      ? `${money(unrecovered)} of ${year} taxes sits on units that were not let when the ` +
        `year opened, in buildings with no other tenant to recover it from.`
      : "";
  const line: RevenueLine = {
    key: "realEstateTaxes",
    label: "Real Estate Taxes",
    qboAccount: "41002 Real Estate Taxes",
    group: BILLABLE_INCOME,
    source: "taxRolls",
    months,
    total: sum(months),
    gap:
      [
        missing > 0
          ? `${missing} assessed unit${missing === 1 ? " has" : "s have"} no ${year} Budget PA, so ${
              missing === 1 ? "it is" : "they are"
            } excluded. Open ${year} on the Tax Rolls tab — it is the source of truth for this line.`
          : "",
        stranded,
      ]
        .filter(Boolean)
        .join(" ") || undefined,
    contributions,
  };
  return { line, perUnit };
}

// ─── Sales-driven lines ──────────────────────────────────────────────────────

/**
 * Next year's sales for one tenant, month by month, from their own history.
 *
 * The base is the most recent COMPLETE calendar year, moved by the tenant's own
 * recent trend and shaped by their own seasonality — a ski-town pub earns its
 * year in three months, and a flat twelfth would put percentage rent in months
 * it will never be billed in.
 *
 * 2020 and 2021 are excluded from the trend. Half the centre was shut, and a
 * business that fell 40% and recovered is not a business trending at -40%.
 * They stay in the record; they just don't get a vote on what comes next.
 *
 * Null when there is no complete year to work from — a tenant who opened in
 * August has no shape to project, and a guess dressed as a projection is worse
 * than an empty line somebody has to fill in.
 */
const PANDEMIC_YEARS = [2020, 2021];
/** A trend beyond this is a change of circumstances, not a rate of growth. */
const MAX_TREND = 0.15;
/**
 * The uplift for a tenant with no trend to read — one complete year, or two
 * that aren't consecutive.
 *
 * 2% because that is what the centre has always budgeted by hand. It is a
 * convention rather than a measurement, which is exactly why it is only used
 * where there is nothing to measure.
 */
const DEFAULT_TREND = 0.02;

function projectSales(
  tenantId: string,
  sales: TenantSalesMonth[],
  budgetYear: number,
): { months: number[]; basedOn: number; growth: number } | null {
  const mine = sales.filter((row) => row.tenantId === tenantId);
  if (mine.length === 0) return null;

  // Complete years only: a part year understates the total and distorts the
  // shape it is being used to predict.
  const byYear = new Map<number, number[]>();
  for (const row of mine) {
    const months = byYear.get(row.year) || zeroes();
    months[row.month - 1] = Number(row.grossSales) || 0;
    byYear.set(row.year, months);
  }
  const complete = [...byYear.entries()]
    .filter(
      ([year]) => year < budgetYear && mine.filter((row) => row.year === year).length === 12,
    )
    .sort(([a], [b]) => a - b);

  if (complete.length === 0) return null;

  const [baseYear, baseMonths] = complete[complete.length - 1];
  const baseTotal = sum(baseMonths);
  if (baseTotal <= 0) return null;

  // The tenant's own trend: the middle of their recent year-on-year changes,
  // which ignores one freak year in a way an average would not.
  const usable = complete.filter(([year]) => !PANDEMIC_YEARS.includes(year));
  const changes: number[] = [];
  for (let i = 1; i < usable.length; i += 1) {
    const [prevYear, prevMonths] = usable[i - 1];
    const [thisYear, thisMonths] = usable[i];
    // Consecutive years only — 2019 against 2022 is three years of change
    // reported as one.
    if (thisYear - prevYear !== 1) continue;
    const before = sum(prevMonths);
    if (before <= 0) continue;
    changes.push(sum(thisMonths) / before - 1);
  }
  changes.sort((a, b) => a - b);
  const median = changes.length
    ? changes.length % 2
      ? changes[(changes.length - 1) / 2]
      : (changes[changes.length / 2 - 1] + changes[changes.length / 2]) / 2
    : DEFAULT_TREND;
  const growth = Math.max(-MAX_TREND, Math.min(MAX_TREND, median));

  // Carried forward one year per year between the base and the budget.
  const factor = Math.pow(1 + growth, Math.max(1, budgetYear - baseYear));
  const shape = baseMonths.map((amount) => amount / baseTotal);
  const projected = baseTotal * factor;

  return { months: shape.map((share) => projected * share), basedOn: baseYear, growth };
}

// Percentage rent: the lease's rate on sales ABOVE the natural breakpoint, and
// nothing below it. Charged in the months it actually falls — a tenant who
// crosses in October pays in October, November and December, which is what the
// cash flow will show. Spreading it evenly would put revenue in January that
// cannot possibly arrive then.
function percentageRentLine(
  year: number,
  /** The January incumbent of each unit — the only tenancies the budget knows. */
  held: Lease[],
  units: Unit[],
  tenants: Tenant[],
  configuration: TospConfiguration,
  sales: TenantSalesMonth[],
): RevenueLine {
  const unitById = (id: string) => units.find((u) => u.id === id);
  const paying = tenants.filter((tenant) =>
    held.some(
      ({ tenant: t, occupancy, unit }) =>
        t.id === tenant.id &&
        (occupancy.percentageRentPayment ?? 0) > 0 &&
        chargesPercentageRent(configuration, unit),
    ),
  );

  const rows: ContributionDraft[] = [];
  const noHistory: string[] = [];

  for (const tenant of paying) {
    const rate =
      (tenant.occupancies || [])
        .map((occupancy) => occupancy.percentageRentPayment)
        .find((value) => (value ?? 0) > 0) ?? 0;
    const breakpoint = tenantBreakpointDetail(tenant, unitById, configuration, year).total;
    const forecast = projectSales(tenant.id, sales, year);

    // A tenant who cannot be projected is still ON this line — they pay
    // percentage rent, and the budget has to say what is expected of them.
    // Listed at nothing rather than left out, so the admin can see who needs a
    // figure entering instead of discovering the omission at the year end.
    if (!forecast || breakpoint === null) {
      noHistory.push(tenantLabel(tenant));
      // Unknown, not nil: nobody can say what Minh's owes on sales nobody has.
      rows.push({
        id: tenant.id,
        label: tenantLabel(tenant),
        months: zeroes(),
        unknown: trues(),
      });
      continue;
    }

    // Walked month by month so the charge lands where the breakpoint is
    // actually crossed: nothing until the running total passes it, the excess
    // in the month it does, and the whole month's sales after that. The twelve
    // add up to the rate on the year's sales above the breakpoint.
    const months = zeroes();
    let running = 0;
    for (let m = 0; m < 12; m += 1) {
      const before = running;
      running += forecast.months[m];
      const above = Math.max(0, running - Math.max(breakpoint, before));
      if (above > 0) months[m] = (above * rate) / 100;
    }

    // Kept even when it comes to nothing: a tenant projected not to reach their
    // breakpoint is a fact about the year, and a row at zero says so. Dropping
    // them would read as though they were not on percentage rent at all.
    rows.push({ id: tenant.id, label: tenantLabel(tenant), months });
  }

  const contributions = byTenant(rows);
  const months = totalOf(contributions);

  return {
    key: "percentageRent",
    label: "Percentage Rent",
    qboAccount: "40003 Percentage Rent",
    group: RENTAL_INCOME,
    source: "sales",
    months,
    total: sum(months),
    gap: noHistory.length
      ? `${noHistory.length} tenant${noHistory.length === 1 ? "" : "s"} on percentage rent ` +
        `cannot be projected — no complete year of sales to work from: ${noHistory.join(", ")}.`
      : undefined,
    contributions,
  };
}

// A straight percentage of projected sales, billed as a flat twelfth.
//
// Unlike percentage rent, there is no breakpoint and no seasonality: BMVA is
// invoiced the same amount every month against a figure agreed at the start of
// the year, and reconciled later against what was actually sold. So the budget
// shows what will actually be billed — twelve equal instalments — rather than
// the shape the sales themselves will take.
function bmvaRoyaltyLine(
  year: number,
  /** The January incumbent of each unit — the only tenancies the budget knows. */
  held: Lease[],
  tenants: Tenant[],
  configuration: TospConfiguration,
  sales: TenantSalesMonth[],
): RevenueLine {
  /**
   * A fixed annual royalty, in place of the rate on sales.
   *
   * The exception the BMVA Cap on the lease records. A tenant who does not
   * report sales can still owe BMVA a royalty — LCBO is the case: the landlord
   * remits a set sum and recovers it inside their merged CAM charge — and
   * without this they were simply absent from the line, so what the centre owes
   * BMVA came out short.
   *
   * Taken from the LEASE, or from any of its spaces — the one box an admin can
   * actually type into is on the leased space card, and the lease's own field
   * has no input at all and is only ever set by lease extraction. Reading only
   * the lease meant a cap entered by hand was never found.
   *
   * The FIRST one found, never the sum. The lease's own note says the cap is
   * "still per tenant": it is one figure for the tenancy, so a tenant holding a
   * shop and a locker owes it once, not once per space.
   *
   * Annual, spread over twelve like every other royalty — which is how BMVA
   * invoices and reconciles it.
   */
  const cappedRoyalty = (tenant: Tenant): number | null => {
    const named = (value: number | null | undefined): boolean =>
      value !== null && value !== undefined && value > 0;

    for (const { tenant: t, occupancy, space } of held) {
      if (t.id !== tenant.id) continue;
      if (named(occupancy.bmvaCap)) return occupancy.bmvaCap as number;
      if (named(space.bmvaCap)) return space.bmvaCap as number;
    }
    return null;
  };

  // The rate is BMVA's, one figure for everybody — a tenant either pays it or
  // pays a cap instead, so what varies here is who's on it, not at what rate.
  const paying = tenants.filter(
    (tenant) =>
      // A capped tenant is on the line whatever they report. The cap IS the
      // royalty, so there is nothing to work out from sales and no reason to
      // ask them for any.
      cappedRoyalty(tenant) !== null ||
      // Otherwise a royalty is a percentage of sales, so a tenant who does not
      // report sales does not pay one — the BACG among them. Listing them here
      // would put a permanent unknown on the line for a fee nobody owes.
      (tenantReportsSales(tenant) &&
        held.some(
          ({ tenant: t, occupancy }) =>
            t.id === tenant.id && (bmvaRoyalty(configuration, occupancy) ?? 0) > 0,
        )),
  );

  const rows: ContributionDraft[] = [];
  const noHistory: string[] = [];

  for (const tenant of paying) {
    // A cap short-circuits the sales maths entirely: the figure is known, so
    // there is no forecast to make and nothing for an admin to fill in.
    const cap = cappedRoyalty(tenant);
    if (cap !== null) {
      rows.push({
        id: tenant.id,
        label: tenantLabel(tenant),
        months: zeroes().map(() => cap / 12),
      });
      continue;
    }

    const rate =
      bmvaRoyalty(
        configuration,
        (tenant.occupancies || []).find(
          (occupancy) => (bmvaRoyalty(configuration, occupancy) ?? 0) > 0,
        ),
      ) ?? 0;
    const forecast = projectSales(tenant.id, sales, year);
    // Same as percentage rent: a tenant with no history still carries the
    // royalty, so they are listed at nothing for an admin to fill in rather
    // than quietly left off the line.
    if (!forecast) {
      noHistory.push(tenantLabel(tenant));
      rows.push({
        id: tenant.id,
        label: tenantLabel(tenant),
        months: zeroes(),
        unknown: trues(),
      });
      continue;
    }

    const annual = (sum(forecast.months) * rate) / 100;
    rows.push({
      id: tenant.id,
      label: tenantLabel(tenant),
      months: zeroes().map(() => annual / 12),
    });
  }

  const contributions = byTenant(rows);
  const months = totalOf(contributions);

  return {
    key: "bmvaRoyalty",
    label: "BMVA Royalty",
    qboAccount: "41003 BMVA Royalty Fee",
    group: BILLABLE_INCOME,
    source: "sales",
    months,
    total: sum(months),
    gap: noHistory.length
      ? `${noHistory.length} tenant${noHistory.length === 1 ? "" : "s"} carrying a royalty ` +
        `cannot be projected — no complete year of sales to work from: ${noHistory.join(", ")}.`
      : undefined,
    contributions,
  };
}

// Area × rate, straight off the unit and with no sales in it — always payable,
// so it is a projected line like the rents rather than an optional extra.
function bmvaBasicFeesLine(
  year: number,
  units: Unit[],
  incumbents: Map<string, Lease>,
  configuration: TospConfiguration,
): { line: RevenueLine; perUnit: Map<string, number[]> } {
  // Charged exactly as the tax recovery is: the dues follow the unit, the
  // tenancy holding it when the year opened pays them for the year, and a unit
  // nobody held is carried by the let units of its building.
  const { rows, perUnit, unrecovered } = recoveryRows(
    units
      .filter((unit) => bmvaAnnualFee(unit, configuration) > 0)
      .map((unit) => ({ unit, annual: bmvaAnnualFee(unit, configuration) })),
    incumbents,
  );

  const contributions = byTenant(rows);
  const months = totalOf(contributions);
  const line: RevenueLine = {
    key: "bmvaBasicFees",
    label: "BMVA Basic Fees",
    qboAccount: "41006 BMVA Basic Fees",
    group: BILLABLE_INCOME,
    source: "lease",
    months,
    total: sum(months),
    gap:
      unrecovered > 0.005
        ? `${money(unrecovered)} of dues sits on units that were not let when ${year} opened, ` +
          `in buildings with no other tenant to recover it from.`
        : undefined,
    contributions,
  };
  return { line, perUnit };
}

// ─── Assembly ────────────────────────────────────────────────────────────────

/**
 * The whole revenue side for one budget year, in the order the QuickBooks
 * chart of accounts lists it — under the same parent accounts, so the screen
 * can be read straight down while keying the figures in.
 */
export function projectRevenue(
  year: number,
  units: Unit[],
  tenants: Tenant[],
  unitLabel: (unit: Unit) => string,
  /** Carries the BMVA rate and royalty, which are portfolio-wide. */
  configuration: TospConfiguration,
  /** Where Budget PA lives — one figure per roll share. */
  taxRolls: TaxRollRecord[],
  /** Reported sales, which percentage rent is projected from. */
  sales: TenantSalesMonth[] = [],
  /**
   * unitId → tenantId, the occupancy the BUDGET states for a unit rather than
   * derives from the dates. Carried on the budget as camOccupancy, and it now
   * governs the two recoveries as well as CAM — a unit cannot be charged to
   * one tenancy for its CAM and another for its taxes.
   */
  assume: Record<string, string> = {},
): RevenueProjection {
  const leases = leasesFor(units, tenants);
  // WHO THE BUDGET KNOWS ABOUT: the tenancy holding each unit when the year
  // opened. Every line is built from these and only these — rent, the two
  // recoveries, and the two sales-driven lines — so a tenant who arrives
  // part-way through the year appears nowhere on the budget, and one who leaves
  // keeps the whole year. The reconciliation settles what actually happened.
  const incumbents = incumbentLeases(year, leases, assume);
  const held = [...incumbents.values()];
  const [baseRent, storageRent] = rentLines(year, leases, incumbents);
  const taxes = realEstateTaxLine(year, units, incumbents, taxRolls);
  const dues = bmvaBasicFeesLine(year, units, incumbents, configuration);

  const lines: RevenueLine[] = [
    baseRent,
    storageRent,
    percentageRentLine(year, held, units, tenants, configuration, sales),
    taxes.line,
    bmvaRoyaltyLine(year, held, tenants, configuration, sales),
    dues.line,
  ];

  // A unit earns nothing on the budget if nobody held it when the year opened.
  // Whoever takes it later is a handover the budget could not have foreseen, so
  // the space really does stand at nil here — which is worth saying plainly
  // rather than leaving an admin to notice the missing rent.
  const earning = new Set(incumbents.keys());

  const months = zeroes().map((_, m) => sum(lines.map((line) => line.months[m])));
  return {
    year,
    lines,
    months,
    total: sum(months),
    unleasedUnits: units.filter((u) => !earning.has(u.id)).map(unitLabel),
    perUnit: { realEstateTaxes: taxes.perUnit, bmvaBasicFees: dues.perUnit },
    incumbents: new Map(
      [...incumbents.entries()].map(([unitId, lease]) => [unitId, lease.tenant.id]),
    ),
  };
}

